import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, FormGroup, Input, Label, Spinner, Table } from "reactstrap";
import moment from "moment";

import { Aviso, EstadoTabela, ModalHub, fieldInputStyle, fmtNumber } from "../_documentosFrete/comum";
import {
  COMPROVANTE_ACCEPT,
  COMPROVANTE_LEGENDA,
  COMPROVANTE_MAX_ARQUIVOS,
  enviarComprovantes,
  listarComprovantes,
  listarNotasDoPedido,
  mensagemErroPedido,
  removerComprovante,
  validarArquivosComprovante,
} from "../../../services/pedidosApi";

// Comprovantes do pedido (H6, PO1-2380): duas fontes numa tela só — anexos
// das ocorrências das NFs (chegam da transportadora, só leitura) e os enviados
// manualmente pelo operador (S3, podem ser excluídos). Validação local no molde
// de freteTabelaAnexosApi (tipo, tamanho, quantidade) antes do request; a URL
// é pré-assinada, então abre direto em nova aba.

const fmtDataHora = (v) => {
  const m = v ? moment(v) : null;
  return m && m.isValid() ? m.format("DD/MM/YYYY HH:mm") : "-";
};
const fmtTamanho = (bytes) => {
  const kb = (Number(bytes) || 0) / 1024;
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};

const abrir = (url) => {
  if (!url) return;
  window.open(url, "_blank", "noopener");
};

export default function ComprovantesModal({ pedido, paramsRecorte, podeEditar, onClose }) {
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [dados, setDados] = useState({ ocorrencias: [], manuais: [], storageDisponivel: true });
  const [notas, setNotas] = useState([]);
  const [idNotaFiscal, setIdNotaFiscal] = useState("");
  const [arquivos, setArquivos] = useState([]);
  const [erroArquivos, setErroArquivos] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [excluindo, setExcluindo] = useState(null); // comprovante em confirmação
  const [excluindoEmCurso, setExcluindoEmCurso] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [alterou, setAlterou] = useState(false);
  const inputRef = useRef(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro("");
    try {
      setDados(await listarComprovantes(pedido));
    } catch (e) {
      setErro(mensagemErroPedido(e, "Não conseguimos carregar os comprovantes."));
    } finally {
      setCarregando(false);
    }
  }, [pedido]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Notas do pedido para o "vincular à NF" do upload (opcional).
  useEffect(() => {
    let ativo = true;
    listarNotasDoPedido({ ...(paramsRecorte || {}), pedido })
      .then((r) => ativo && setNotas(r))
      .catch(() => ativo && setNotas([]));
    return () => {
      ativo = false;
    };
  }, [pedido, paramsRecorte]);

  const escolherArquivos = (e) => {
    // React 16: lê os arquivos do evento antes de qualquer setState.
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    // Só os NOVOS são validados, contra o espaço que resta: os já escolhidos
    // ficam, e apenas os excedentes são rejeitados (QA 12/10).
    const { validos, erros } = validarArquivosComprovante(files, arquivos.length);
    setArquivos(arquivos.concat(validos));
    setErroArquivos(erros.join(" "));
  };

  const enviar = async () => {
    if (enviando || !arquivos.length) return;
    setEnviando(true);
    setAviso(null);
    try {
      const r = await enviarComprovantes({ pedido, idNotaFiscal: idNotaFiscal || null, arquivos });
      setAviso({ tom: "ok", texto: `${fmtNumber(r.enviados || arquivos.length)} comprovante(s) anexado(s).` });
      setArquivos([]);
      setErroArquivos("");
      setAlterou(true);
      await carregar();
    } catch (e) {
      setAviso({ tom: "erro", texto: mensagemErroPedido(e, "Não foi possível enviar os comprovantes.") });
    } finally {
      setEnviando(false);
    }
  };

  const confirmarExclusao = async () => {
    if (!excluindo || excluindoEmCurso) return;
    const alvo = excluindo;
    setExcluindoEmCurso(true);
    setAviso(null);
    try {
      await removerComprovante(alvo.id);
      setAviso({ tom: "ok", texto: `Comprovante ${alvo.nomeArquivo} excluído.` });
      setAlterou(true);
      setExcluindo(null);
      await carregar();
    } catch (e) {
      setAviso({ tom: "erro", texto: mensagemErroPedido(e, "Não foi possível excluir o comprovante.") });
      setExcluindo(null);
    } finally {
      setExcluindoEmCurso(false);
    }
  };

  const fechar = () => {
    if (enviando || excluindoEmCurso) return;
    onClose(alterou);
  };

  const ocorrencias = dados.ocorrencias || [];
  const manuais = dados.manuais || [];
  const semStorage = dados.storageDisponivel === false;

  return (
    <ModalHub titulo={`Comprovantes · pedido ${pedido}`} icone="tim-icons icon-paper" onClose={fechar} maxWidth={900}>
      {erro ? <Aviso tom="erro">{erro}</Aviso> : null}
      {aviso ? <Aviso tom={aviso.tom}>{aviso.texto}</Aviso> : null}
      {carregando ? <EstadoTabela loading textoCarregando="Carregando os comprovantes..." /> : null}

      {!carregando && !erro ? (
        <div className="docs-frete">
          <p className="docs-frete__secao">Comprovantes das ocorrências · {fmtNumber(ocorrencias.length)}</p>
          {!ocorrencias.length ? (
            <div className="docs-frete__estado" style={{ minHeight: 60 }}>
              Nenhuma ocorrência com anexo nas notas deste pedido.
            </div>
          ) : (
            <Table className="docs-frete__tabela hub-thead-accent" size="sm" responsive>
              <thead className="text-primary">
                <tr>
                  <th>Nota fiscal</th>
                  <th>Ocorrência</th>
                  <th>Data</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {ocorrencias.map((o) => (
                  <tr key={`oc-${o.id}`}>
                    <td>
                      <strong>{o.numero || "-"}</strong>
                    </td>
                    <td>
                      {o.codigo ? `${o.codigo} · ` : ""}
                      {o.ocorrencia || "-"}
                    </td>
                    <td className="docs-frete__nowrap">{fmtDataHora(o.data)}</td>
                    <td>
                      <div className="docs-frete__acoes">
                        <Button color="primary" size="sm" className="btn-simple" disabled={!o.url} onClick={() => abrir(o.url)}>
                          Abrir
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <p className="docs-frete__secao" style={{ marginTop: "1.25rem" }}>
            Enviados pela operação · {fmtNumber(manuais.length)}
          </p>
          {excluindo ? (
            <Aviso tom="aviso" role="alertdialog">
              Excluir o comprovante <strong>{excluindo.nomeArquivo}</strong>?
              <div className="docs-frete__acoes">
                <Button color="danger" size="sm" onClick={confirmarExclusao} disabled={excluindoEmCurso}>
                  {excluindoEmCurso ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
                  {excluindoEmCurso ? "Excluindo..." : "Confirmar exclusão"}
                </Button>
                <Button color="default" size="sm" outline onClick={() => setExcluindo(null)} disabled={excluindoEmCurso}>
                  Voltar
                </Button>
              </div>
            </Aviso>
          ) : null}
          {!manuais.length ? (
            <div className="docs-frete__estado" style={{ minHeight: 60 }}>
              Nenhum comprovante enviado manualmente.
            </div>
          ) : (
            <Table className="docs-frete__tabela hub-thead-accent" size="sm" responsive>
              <thead className="text-primary">
                <tr>
                  <th>Arquivo</th>
                  <th>Nota fiscal</th>
                  <th>Enviado em</th>
                  <th>Por</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {manuais.map((c) => (
                  <tr key={`man-${c.id}`}>
                    <td>
                      <strong>{c.nomeArquivo}</strong>
                      <small>{fmtTamanho(c.tamanho)}</small>
                    </td>
                    <td>{c.idNotaFiscal ? (notas.find((n) => Number(n.id) === Number(c.idNotaFiscal)) || {}).numero || `#${c.idNotaFiscal}` : "-"}</td>
                    <td className="docs-frete__nowrap">{fmtDataHora(c.creationTime)}</td>
                    <td>{c.usuario || "-"}</td>
                    <td>
                      <div className="docs-frete__acoes">
                        <Button color="primary" size="sm" className="btn-simple" disabled={!c.url} onClick={() => abrir(c.url)}>
                          Abrir
                        </Button>
                        {podeEditar ? (
                          <Button color="danger" size="sm" className="btn-simple" onClick={() => setExcluindo(c)} disabled={excluindoEmCurso}>
                            Excluir
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {podeEditar ? (
            <div className="docs-frete__form" style={{ marginTop: "1.25rem" }}>
              <p className="docs-frete__secao">Anexar comprovante</p>
              {semStorage ? <Aviso tom="aviso">Armazenamento de anexos não configurado no servidor. O envio está indisponível.</Aviso> : null}
              <FormGroup>
                <Label for="pedido-comprovante-nf">Nota fiscal (opcional)</Label>
                <Input
                  id="pedido-comprovante-nf"
                  type="select"
                  value={idNotaFiscal}
                  onChange={(e) => setIdNotaFiscal(e.target.value)}
                  disabled={enviando || semStorage}
                  style={fieldInputStyle}
                >
                  <option value="">Pedido inteiro</option>
                  {notas.map((n) => (
                    <option key={n.id} value={n.id}>
                      NF {n.numero}
                      {n.serie ? ` · série ${n.serie}` : ""}
                    </option>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup>
                <Label>Arquivos · {COMPROVANTE_LEGENDA}</Label>
                <div>
                  <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept={COMPROVANTE_ACCEPT}
                    style={{ display: "none" }}
                    aria-label="Escolher comprovantes"
                    disabled={enviando || semStorage}
                    onChange={escolherArquivos}
                  />
                  <Button
                    outline
                    color="primary"
                    size="sm"
                    disabled={enviando || semStorage || arquivos.length >= COMPROVANTE_MAX_ARQUIVOS}
                    onClick={() => inputRef.current && inputRef.current.click()}
                  >
                    <i className="tim-icons icon-cloud-upload-94" />{" "}
                    {arquivos.length >= COMPROVANTE_MAX_ARQUIVOS ? `Limite de ${COMPROVANTE_MAX_ARQUIVOS} atingido` : "Selecionar arquivos"}
                  </Button>
                </div>
                {erroArquivos ? <span className="docs-frete__erro-campo">{erroArquivos}</span> : null}
                {arquivos.length ? (
                  <ul className="pedidos__anexos" aria-label="Arquivos escolhidos">
                    {arquivos.map((f, i) => (
                      <li key={`${f.name}-${i}`}>
                        <span>
                          {f.name} <em>({fmtTamanho(f.size)})</em>
                        </span>
                        <button
                          type="button"
                          className="pedidos__anexo-remover"
                          aria-label={`Remover ${f.name}`}
                          disabled={enviando}
                          onClick={() => setArquivos(arquivos.filter((_, idx) => idx !== i))}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </FormGroup>
              <div className="docs-frete__acoes" style={{ justifyContent: "flex-start" }}>
                <Button color="primary" size="sm" onClick={enviar} disabled={enviando || semStorage || !arquivos.length}>
                  {enviando ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
                  {enviando ? "Enviando..." : "Enviar comprovante(s)"}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="docs-frete__modal-rodape">
        <Button color="secondary" size="sm" onClick={fechar} disabled={enviando || excluindoEmCurso}>
          Fechar
        </Button>
      </div>
    </ModalHub>
  );
}
